import React, { FunctionComponent } from 'react';

import { connect } from 'react-redux';
import AppState from '@webapp/store/state';

import styled, { useTheme } from 'styled-components';

import { Translate } from 'react-localize-redux';

import { numberFormatter } from './utils';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import TitleWithHelper from '@components/title-w-helper';
import Percentage from '@components/dashboard/metrics-widget/percentage';
import RefIndicator from '@components/dashboard/metrics-widget/ref-indicator';
import ComingSoonPill from '@components/basics/coming-soon-pill';

// TODO: DEVTEAM-405 - This is for demo only. We need to integrate with real data using the translations.
const EXAMPLE_DATA = [
  {
    id: 'casual',
    label: <Translate id="ANALYTICS.FANS.FAN_TYPE.ITEM.CASUAL" />,
    value: 543211,
    percentage: 3,
  },
  {
    id: 'superfans',
    label: <Translate id="ANALYTICS.FANS.FAN_TYPE.ITEM.SUPERFANS" />,
    value: 132866,
    percentage: -1,
  },
  {
    id: 'contacts',
    label: <Translate id="ANALYTICS.FANS.FAN_TYPE.ITEM.CONTACTS" />,
    value: 54123,
    percentage: 2,
  },
];

const COMING_SOON_DATA = [
  {
    id: 'casual',
    label: <Translate id="ANALYTICS.FANS.FAN_TYPE.ITEM.CASUAL" />,
    value: 25,
    percentage: 0,
  },
  {
    id: 'superfans',
    label: <Translate id="ANALYTICS.FANS.FAN_TYPE.ITEM.SUPERFANS" />,
    value: 25,
    percentage: 0,
  },
  {
    id: 'contacts',
    label: <Translate id="ANALYTICS.FANS.FAN_TYPE.ITEM.CONTACTS" />,
    value: 25,
    percentage: 0,
  },
];

interface FanTypeBoxProps {
  className?: string;
  isMobile: boolean;
  isComingSoon?: boolean;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
});

const FanTypeBox: FunctionComponent<FanTypeBoxProps> = ({
  className,
  isMobile,
  isComingSoon,
}) => {
  const theme = useTheme();
  const getLineWidth = (value: number) => {
    const total = EXAMPLE_DATA.reduce(
      (previousValue, currentValue) => previousValue + currentValue.value,
      1
    );

    return (value / total) * 100;
  };

  return (
    <Box className={className || ''} noBorderTop noBorderBottom>
      <Flex
        className="title-wrapper"
        justifyContent="space-between"
        alignItems="center"
        gap={10}
      >
        <TitleWithHelper
          id="fanTypeTitle"
          title={<Translate id="ANALYTICS.FANS.FAN_TYPE.TITLE" />}
          // helperText={<Translate id="ANALYTICS.FANS.FAN_TYPE.TITLE.HELPER" />}
          isGrey={isComingSoon}
        />
        {isComingSoon && <ComingSoonPill />}
      </Flex>
      <Flex
        className="data"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent={isMobile ? 'flex-start' : 'space-between'}
        alignItems={isMobile ? 'flex-start' : 'center'}
        gap={20}
      >
        {(isComingSoon ? COMING_SOON_DATA : EXAMPLE_DATA).map((type, i) => (
          <Flex
            flexDirection={isMobile ? 'row' : 'column'}
            justifyContent={isMobile ? 'space-between' : 'flex-start'}
            alignItems={isMobile ? 'center' : 'flex-start'}
            key={type.id}
            gap={6}
            style={{ width: '100%' }}
          >
            <RefIndicator
              circleOpacity={1 / (i + 1)}
              label={type.label}
              isBorder={isComingSoon}
            />
            <Flex justifyContent="flex-start" alignItems="center" gap={6}>
              <Typography
                type="small"
                weight="700"
                color={isComingSoon ? theme.colors.tertiary : undefined}
              >
                {isComingSoon ? '—' : numberFormatter.format(type.value)}
              </Typography>
              {!isComingSoon && (
                <Percentage
                  value={type.percentage}
                  valueFormat="percentage"
                  noAnimation
                  noBackground
                />
              )}
            </Flex>
          </Flex>
        ))}
      </Flex>
      <Flex justifyContent="space-between" alignItems="center">
        {/* TODO: DEVTEAM-405 - This is demo data only. We need to use real data. */}
        {(isComingSoon ? COMING_SOON_DATA : EXAMPLE_DATA).map((item, i) => (
          <Line
            className={isComingSoon ? 'grey' : ''}
            key={i}
            style={{
              opacity: 1 / (i + 1),
              width: `${getLineWidth(item.value)}%`,
            }}
          />
        ))}
      </Flex>
    </Box>
  );
};

export default connect(mapStateToProps)(styled(FanTypeBox)`
  /* height: fit-content; */

  .title-wrapper {
    margin-bottom: 20px;
  }

  .data {
    margin-bottom: 20px;
  }
`);

const Line = styled.span`
  height: 3px;
  min-width: 3px;
  flex-grow: 1;
  flex-shrink: 1;
  background-color: ${({ theme }) => theme.colors.blueColor};

  &.grey {
    background-color: ${({ theme }) => theme.colors.tertiary};
  }
`;
